import React, { useEffect, useState } from "react";
import { fetchAuthSession } from "@aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/layouts/MainLayout/MainLayout";
import env from "../../../config/env";

const DEFAULT_WALL_BASE = "https://nbv4ejg6u4.execute-api.us-east-1.amazonaws.com/Prod/wall";
const SUPPORT_REQUEST_TYPES = [
  { key: "desahogo", label: "Solo desahogarme" },
  { key: "aliento", label: "Palabras de aliento" },
  { key: "practico", label: "Sugerencias prácticas" },
  { key: "acompanamiento", label: "No estoy bien, solo acompañamiento" }
];
const AVAILABILITY_STATES = [
  { key: "available", label: "🟢 Disponible para escuchar" },
  { key: "low_energy", label: "🟡 Con poca energía" },
  { key: "unavailable", label: "🔴 No disponible, me estoy cuidando" }
];

const SUPPORT_MODES = [
  { key: "support_from_me", label: "Puedo apoyarte" },
  { key: "support_to_me", label: "Me puedes apoyar" },
  { key: "mutual", label: "Apoyo mutuo" }
];

const getBase = () => env.WALL_API_URL?.replace(/\/$/, "") || DEFAULT_WALL_BASE;

const buildHeaders = async (body) => {
  let auth = {};
  try {
    const tokens = (await fetchAuthSession()).tokens;
    const idToken = tokens?.idToken?.toString();
    if (idToken) auth = { Authorization: `Bearer ${idToken}` };
  } catch (e) {
    console.error("SupportNetwork: no auth", e);
  }
  return {
    ...(body ? { "Content-Type": "application/json" } : {}),
    ...(env.API_KEY ? { "x-api-key": env.API_KEY } : {}),
    ...auth
  };
};

async function api(path, opts = {}) {
  const headers = await buildHeaders(opts.body);
  const res = await fetch(`${getBase()}${path}`, {
    ...opts,
    headers,
    mode: "cors",
    cache: "no-cache"
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  if (res.status === 204) return null;
  return res.json();
}

const SupportNetwork = () => {
  const navigate = useNavigate();
  const [availability, setAvailability] = useState("available");
  const [requestType, setRequestType] = useState("");
  const [invitesIncoming, setInvitesIncoming] = useState([]);
  const [invitesOutgoing, setInvitesOutgoing] = useState([]);
  const [network, setNetwork] = useState({ supporters: [], supporting: [], mutual: [] });
  const [targetUser, setTargetUser] = useState("");
  const [mode, setMode] = useState("mutual");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [profileMap, setProfileMap] = useState({});

  const loadData = async () => {
    setError(null);
    try {
      const [incoming, outgoing, net, profile] = await Promise.all([
        api("/support/invites?direction=incoming&status=pending"),
        api("/support/invites?direction=outgoing&status=pending"),
        api("/support/network"),
        api("/user/profile")
      ]);
      setInvitesIncoming(incoming.items || []);
      setInvitesOutgoing(outgoing.items || []);
      setNetwork(net || { supporters: [], supporting: [], mutual: [] });
      setAvailability(profile?.availability_status || "available");
    } catch (e) {
      console.error(e);
      setError("No se pudo cargar la red de apoyo");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Cargar perfiles para ids en la red (nombre, avatar, meta)
  useEffect(() => {
    const ids = Array.from(
      new Set([
        ...(network.supporters || []),
        ...(network.supporting || []),
        ...(network.mutual || []),
      ])
    ).filter((id) => id && !profileMap[id]);
    if (!ids.length) return;
    const loadProfiles = async () => {
      const entries = await Promise.all(
        ids.map(async (id) => {
          try {
            const data = await api(`/users/${id}/profile`);
            return [id, data || {}];
          } catch (e) {
            return [id, {}];
          }
        })
      );
      setProfileMap((prev) => {
        const next = { ...prev };
        entries.forEach(([id, data]) => (next[id] = data));
        return next;
      });
    };
    loadProfiles();
  }, [network, profileMap]);

  const handleAvailability = async (state) => {
    try {
      setAvailability(state);
      await api("/support/availability", { method: "POST", body: JSON.stringify({ state }) });
    } catch (e) {
      console.error(e);
      setError("No se pudo actualizar disponibilidad");
    }
  };

  const handleSupportRequest = async () => {
    if (!requestType) return;
    setLoading(true);
    try {
      await api("/support/request", { method: "POST", body: JSON.stringify({ type: requestType }) });
      setSuccessMsg("Solicitud de apoyo enviada");
      setRequestType("");
    } catch (e) {
      console.error(e);
      setError("No se pudo enviar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!targetUser.trim()) return;
    setLoading(true);
    try {
      await api("/support/invite", {
        method: "POST",
        body: JSON.stringify({ target_user_id: targetUser.trim(), mode })
      });
      setTargetUser("");
      loadData();
    } catch (e) {
      console.error(e);
      setError("No se pudo enviar la invitación");
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (contactId, action) => {
    setLoading(true);
    try {
      await api(`/support/invites/${contactId}/respond`, {
        method: "POST",
        body: JSON.stringify({ action })
      });
      loadData();
    } catch (e) {
      console.error(e);
      setError("No se pudo responder la invitación");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="flex flex-col" style={{ minHeight: "calc(100vh - 74px)" }}>
        <main className="flex-1 p-6 md:p-8 bg-gray-50 space-y-6 overflow-y-auto">
          <h1 className="text-2xl font-bold">Red de apoyo emocional</h1>

          {error && <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-xl">{error}</div>}
          {successMsg && (
            <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-xl">
              {successMsg}
            </div>
          )}

          <section className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
            <h2 className="text-lg font-semibold">Disponibilidad emocional</h2>
            <div className="flex flex-wrap gap-2">
              {AVAILABILITY_STATES.map((s) => (
                <button
                  key={s.key}
                  className={`px-3 py-2 rounded-full border text-sm ${
                    availability === s.key ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "border-gray-200 hover:bg-gray-100"
                  }`}
                  onClick={() => handleAvailability(s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
            <h2 className="text-lg font-semibold">Solicitud de apoyo del día</h2>
            <div className="flex flex-wrap gap-2">
              {SUPPORT_REQUEST_TYPES.map((t) => (
                <button
                  key={t.key}
                  className={`px-3 py-2 rounded-full border text-sm ${
                    requestType === t.key ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "border-gray-200 hover:bg-gray-100"
                  }`}
                  onClick={() => setRequestType(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <button
              className={`px-4 py-2 rounded-lg text-white ${loading ? "bg-gray-400" : "bg-indigo-600 hover:bg-indigo-700"}`}
              onClick={handleSupportRequest}
              disabled={loading || !requestType}
            >
              Enviar solicitud
            </button>
          </section>

          <section className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
            <h2 className="text-lg font-semibold">Invitar a mi círculo de apoyo</h2>
            <div className="flex flex-wrap gap-2 items-center">
              <input
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-[200px]"
                placeholder="Ingresa el ID del usuario"
                value={targetUser}
                onChange={(e) => setTargetUser(e.target.value)}
              />
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
              >
                {SUPPORT_MODES.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.label}
                  </option>
                ))}
              </select>
              <button
                className={`px-4 py-2 rounded-lg text-white ${loading ? "bg-gray-400" : "bg-emerald-600 hover:bg-emerald-700"}`}
                onClick={handleInvite}
                disabled={loading || !targetUser.trim()}
              >
                Invitar
              </button>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
            <h2 className="text-lg font-semibold">Invitaciones pendientes</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-2">Recibidas</h3>
                {invitesIncoming.length === 0 && <p className="text-sm text-gray-500">Sin invitaciones</p>}
                {invitesIncoming.map((inv) => {
                  const fromId = inv.requester === inv.contact_id ? inv.contact_id : inv.user_id;
                  const modeMeta = {
                    support_from_me: { icon: "🤝", label: "Puedo apoyarte" },
                    support_to_me: { icon: "💚", label: "Me puedes apoyar" },
                    mutual: { icon: "🌿", label: "Apoyo mutuo" }
                  }[inv.mode] || { icon: "🤝", label: inv.mode };
                  return (
                    <div
                      key={`${inv.user_id}-${inv.contact_id}`}
                      className="flex items-center gap-3 border border-gray-200 rounded-2xl px-3 py-2 mb-2 bg-white shadow-sm"
                    >
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold">
                          {(inv.user_name || fromId || "U").slice(0, 1).toUpperCase()}
                        </div>
                      </div>
                      <div className="flex-1 text-sm">
                        <p className="font-semibold text-gray-900">{inv.user_name || fromId}</p>
                        <p className="text-xs text-gray-500">{inv.goal_text || "Quiere conectar para apoyo emocional."}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="group relative">
                          <div className="w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center">
                            <span className="text-lg">{modeMeta.icon}</span>
                          </div>
                          <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap rounded-md bg-green-100 text-green-800 text-xs px-2 py-1 opacity-0 group-hover:opacity-100 shadow-sm">
                            {modeMeta.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            className="group relative w-10 h-10 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center hover:bg-emerald-100"
                            onClick={() => handleRespond(inv.contact_id || inv.user_id, "accept")}
                            aria-label="Aceptar solicitud"
                          >
                            ✅
                            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap rounded-md bg-green-100 text-green-800 text-xs px-2 py-1 opacity-0 group-hover:opacity-100 shadow-sm">
                              Aceptar solicitud
                            </span>
                          </button>
                          <button
                            className="group relative w-10 h-10 rounded-full bg-red-50 text-red-700 border border-red-200 flex items-center justify-center hover:bg-red-100"
                            onClick={() => handleRespond(inv.contact_id || inv.user_id, "reject")}
                            aria-label="Rechazar solicitud"
                          >
                            ❌
                            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap rounded-md bg-red-100 text-red-700 text-xs px-2 py-1 opacity-0 group-hover:opacity-100 shadow-sm">
                              Rechazar solicitud
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div>
                <h3 className="font-semibold text-sm text-gray-700 mb-2">Enviadas</h3>
                {invitesOutgoing.length === 0 && <p className="text-sm text-gray-500">Sin invitaciones</p>}
                {invitesOutgoing.map((inv) => (
                  <div key={`${inv.user_id}-${inv.contact_id}`} className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 mb-2">
                    <div className="text-sm">
                      <p className="font-semibold">Para: {inv.contact_id}</p>
                      <p className="text-xs text-gray-500">Modo: {inv.mode}</p>
                    </div>
                    <span className="text-xs text-gray-500">{inv.status}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="bg-white rounded-2xl border border-gray-200 p-4 space-y-2">
            <h2 className="text-lg font-semibold">Mi red</h2>
            <div className="grid md:grid-cols-3 gap-3 text-sm">
              {[
                { title: "Personas que me apoyan", list: network.supporters },
                { title: "Personas a las que apoyo", list: network.supporting },
                { title: "Apoyo mutuo", list: network.mutual },
              ].map((block) => (
                <div key={block.title} className="border border-gray-200 rounded-xl p-3 space-y-2 bg-white shadow-sm">
                  <p className="font-semibold text-gray-700 mb-1">{block.title}</p>
                  {block.list?.length ? (
                    block.list.map((u) => {
                      const prof = profileMap[u] || {};
                      const displayName = prof.user_name || prof.name || prof.email || u;
                      const goal = prof.goal_text || "Objetivo diario no definido";
                      const availabilityLabel =
                        AVAILABILITY_STATES.find((s) => s.key === prof.availability_status)?.label ||
                        "Disponibilidad no indicada";
                      return (
                        <div
                          key={u}
                          className="flex items-center gap-3 rounded-lg border border-gray-100 p-2 hover:shadow cursor-pointer"
                          onClick={() => navigate(`/support/user/${u}`)}
                        >
                          <img
                            src={prof.avatar_url}
                            alt={displayName}
                            className="w-10 h-10 rounded-full object-cover border border-gray-200"
                            onError={(e) => {
                              e.currentTarget.src = "";
                              e.currentTarget.alt = "avatar";
                            }}
                          />
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900 text-sm">
                              {displayName}
                            </p>
                            <p className="text-xs text-gray-600">
                              {goal}
                            </p>
                            <p className="text-xs text-emerald-700">
                              {availabilityLabel}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-500">Aún no tienes</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>
    </MainLayout>
  );
};

export default SupportNetwork;
